import { useRef, useCallback, useState, useEffect } from 'react';
import { Play, Pause, SkipBack, SkipForward, SidebarSimple, BracketsCurly, Rows, Wind } from '@phosphor-icons/react';
import { usePlaybackStore } from '../../stores/playbackStore';
import { useSceneStore } from '../../stores/sceneStore';
import { useAnimationController } from '../../hooks/useAnimationController';
import { useTimelineStore } from '../../stores/timelineStore';
import { getSpineManager } from '../../lib/spine-manager';
import { Tooltip } from '../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Slider } from '../ui/slider';

// Assign a color to an event name via simple hash
const EVENT_COLORS = ['#f97316', '#10b981', '#f43f5e', '#8b5cf6', '#06b6d4', '#eab308'];
function eventColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return EVENT_COLORS[h % EVENT_COLORS.length];
}

interface PlaybackBarProps {
  onToggleLeft?: () => void;
  onToggleRight?: () => void;
  leftOpen?: boolean;
  rightOpen?: boolean;
}

export default function PlaybackBar({ onToggleLeft, onToggleRight, leftOpen, rightOpen }: PlaybackBarProps) {
  const {
    isPlaying, togglePlay, stepForward, stepBackward,
    currentFrame, totalFrames, speed, setSpeed,
    loopStart, loopEnd, setLoopStart, setLoopEnd, clearLoop,
    globalWind, globalGravity, setGlobalWind, setGlobalGravity,
  } = usePlaybackStore();

  function handleGlobalWind(wind: number) {
    setGlobalWind(wind);
    getSpineManager().setGlobalWind(wind);
  }

  function handleGlobalGravity(gravity: number) {
    setGlobalGravity(gravity);
    getSpineManager().setGlobalGravity(gravity);
  }

  const envActive = globalWind !== 0 || globalGravity !== 0;
  const { handleSeek } = useAnimationController();
  const { selectedLayerId } = useSceneStore();
  const { visible: timelineVisible, toggle: toggleTimeline } = useTimelineStore();

  const timelineRef = useRef<HTMLDivElement>(null);
  const loopActive = loopStart !== null && loopEnd !== null;

  // Event markers: fetch when layer or totalFrames changes (totalFrames signals animation loaded)
  const [eventMarkers, setEventMarkers] = useState<Array<{ time: number; name: string }>>([]);
  useEffect(() => {
    if (!selectedLayerId || totalFrames <= 0) { setEventMarkers([]); return; }
    const markers = getSpineManager().getEventMarkers(selectedLayerId, 0);
    setEventMarkers(markers);
  }, [selectedLayerId, totalFrames]);

  function pctFromFrame(frame: number) {
    return totalFrames > 0 ? (frame / totalFrames) * 100 : 0;
  }

  function frameFromMouseX(clientX: number) {
    const el = timelineRef.current;
    if (!el || totalFrames <= 0) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(ratio * totalFrames);
  }

  function enableLoop() {
    const total = usePlaybackStore.getState().totalFrames;
    if (total <= 0) return;
    const quarter = Math.round(total * 0.25);
    const three = Math.round(total * 0.75);
    setLoopStart(quarter);
    setLoopEnd(three);
  }

  const startDragHandle = useCallback((which: 'start' | 'end') => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const onMove = (ev: MouseEvent) => {
      const frame = frameFromMouseX(ev.clientX);
      if (which === 'start') {
        const end = usePlaybackStore.getState().loopEnd ?? totalFrames;
        setLoopStart(Math.min(frame, end - 1));
      } else {
        const start = usePlaybackStore.getState().loopStart ?? 0;
        setLoopEnd(Math.max(frame, start + 1));
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalFrames]);

  return (
    <div className="h-12 bg-bg-surface border-t border-border-subtle px-3 flex items-center gap-3" data-testid="playback-bar">

      {/* Left panel toggle — mobile/tablet only (xl:hidden) */}
      {onToggleLeft && (
        <button
          onClick={onToggleLeft}
          className={`xl:hidden w-[30px] h-[30px] flex items-center justify-center rounded-md transition-all duration-normal shrink-0 ${
            leftOpen
              ? 'bg-accent-muted border border-accent/30 text-accent'
              : 'bg-bg-input border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover'
          }`}
          title="Toggle left panel"
          data-testid="mobile-toggle-left"
        >
          <SidebarSimple size={14} />
        </button>
      )}

      {/* Playback controls */}
      <div className="flex items-center gap-1">
        <Tooltip content="Step backward (←)">
          <button
            onClick={stepBackward}
            data-testid="playback-step-back"
            className="w-[30px] h-[30px] flex items-center justify-center rounded-md bg-bg-input border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-normal"
          >
            <SkipBack size={14} weight="fill" />
          </button>
        </Tooltip>

        <Tooltip content={isPlaying ? 'Pause (Space)' : 'Play (Space)'}>
          <button
            onClick={togglePlay}
            data-testid="playback-play"
            className="w-[38px] h-[30px] flex items-center justify-center rounded-md bg-accent hover:bg-accent-hover text-white transition-all duration-normal hover:shadow-glow shrink-0"
          >
            {isPlaying ? <Pause size={14} weight="fill" /> : <Play size={14} weight="fill" />}
          </button>
        </Tooltip>

        <Tooltip content="Step forward (→)">
          <button
            onClick={stepForward}
            data-testid="playback-step-forward"
            className="w-[30px] h-[30px] flex items-center justify-center rounded-md bg-bg-input border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-all duration-normal"
          >
            <SkipForward size={14} weight="fill" />
          </button>
        </Tooltip>
      </div>

      {/* Timeline */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div ref={timelineRef} className="flex-1 relative flex items-center h-5">
          {/* Base track */}
          <input
            type="range"
            min={0}
            max={totalFrames || 100}
            value={currentFrame}
            step={1}
            className="w-full h-1 rounded-full appearance-none bg-bg-active cursor-pointer absolute inset-x-0
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-text-primary
              [&::-webkit-slider-thumb]:border-[2.5px] [&::-webkit-slider-thumb]:border-accent
              [&::-webkit-slider-thumb]:shadow-sm
              [&::-webkit-slider-thumb]:transition-transform
              [&::-webkit-slider-thumb]:hover:scale-110
              [&::-webkit-slider-thumb]:hover:shadow-glow
              [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:z-10"
            onChange={(e) => handleSeek(parseInt(e.target.value))}
          />

          {/* Event markers */}
          {eventMarkers.map((marker, i) => {
            const fps = usePlaybackStore.getState().fps;
            const pct = totalFrames > 0 ? (marker.time * fps / totalFrames) * 100 : 0;
            return (
              <Tooltip key={`${marker.name}-${i}`} content={marker.name}>
                <div
                  data-testid="event-marker"
                  className="absolute pointer-events-auto z-30 cursor-default"
                  style={{ left: `calc(${pct}% - 4px)`, top: '-2px' }}
                >
                  <div
                    className="w-2 h-2 rounded-full border border-white/40 shadow-sm"
                    style={{ backgroundColor: eventColor(marker.name) }}
                  />
                </div>
              </Tooltip>
            );
          })}

          {/* Loop region overlay */}
          {loopActive && (
            <>
              {/* Tinted fill between in/out points */}
              <div
                className="absolute pointer-events-none rounded-sm"
                style={{
                  left: `${pctFromFrame(loopStart!)}%`,
                  width: `${pctFromFrame(loopEnd!) - pctFromFrame(loopStart!)}%`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '4px',
                  backgroundColor: 'rgba(124, 58, 237, 0.45)',
                }}
              />

              {/* In-point handle (left bracket) */}
              <div
                data-testid="loop-handle-start"
                className="absolute top-0 bottom-0 flex items-center justify-center cursor-ew-resize z-20 group"
                style={{ left: `calc(${pctFromFrame(loopStart!)}% - 5px)`, width: '10px' }}
                onMouseDown={startDragHandle('start')}
              >
                <div className="w-[3px] h-4 rounded-sm bg-accent group-hover:bg-accent-hover transition-colors" />
              </div>

              {/* Out-point handle (right bracket) */}
              <div
                data-testid="loop-handle-end"
                className="absolute top-0 bottom-0 flex items-center justify-center cursor-ew-resize z-20 group"
                style={{ left: `calc(${pctFromFrame(loopEnd!)}% - 5px)`, width: '10px' }}
                onMouseDown={startDragHandle('end')}
              >
                <div className="w-[3px] h-4 rounded-sm bg-accent group-hover:bg-accent-hover transition-colors" />
              </div>
            </>
          )}
        </div>
        <span className="font-mono text-xs text-text-secondary shrink-0 tabular-nums">
          {totalFrames > 0 ? `${currentFrame} / ${totalFrames}` : '— / —'}
        </span>
      </div>

      {/* Speed */}
      <div className="flex items-center gap-1 shrink-0">
          {[0.25, 0.5, 1, 2, 4].map((s) => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              className={`px-[6px] py-[3px] rounded text-xs font-mono transition-all duration-fast ${
                speed === s
                  ? 'bg-accent-muted text-accent border border-accent/30'
                  : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              {s}×
            </button>
          ))}
      </div>

      {/* Global physics environment */}
      <Popover>
        <Tooltip content="Global wind & gravity">
          <PopoverTrigger asChild>
            <button
              className={`flex items-center gap-1.5 px-2 py-[5px] rounded-md text-xs font-sans transition-all duration-fast shrink-0 ${
                envActive
                  ? 'bg-accent-muted text-accent border border-accent/30'
                  : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              <Wind size={13} />
              {envActive && (
                <span className="font-mono hidden lg:block">
                  {globalWind !== 0 ? `W${globalWind > 0 ? '+' : ''}${globalWind}` : ''}
                  {globalWind !== 0 && globalGravity !== 0 ? ' ' : ''}
                  {globalGravity !== 0 ? `G${globalGravity > 0 ? '+' : ''}${globalGravity}` : ''}
                </span>
              )}
              {!envActive && <span className="hidden lg:block">Env</span>}
            </button>
          </PopoverTrigger>
        </Tooltip>
        <PopoverContent align="center" side="top" className="w-56 space-y-4">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="font-sans text-xs text-text-secondary">Wind</span>
              <span className="font-mono text-xs text-text-tertiary">{globalWind.toFixed(0)}</span>
            </div>
            <Slider
              min={-50} max={50} step={1}
              value={[globalWind]}
              onValueChange={([v]) => handleGlobalWind(v)}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="font-sans text-xs text-text-secondary">Gravity</span>
              <span className="font-mono text-xs text-text-tertiary">{globalGravity.toFixed(0)}</span>
            </div>
            <Slider
              min={-50} max={50} step={1}
              value={[globalGravity]}
              onValueChange={([v]) => handleGlobalGravity(v)}
            />
          </div>
          {envActive && (
            <button
              onClick={() => { handleGlobalWind(0); handleGlobalGravity(0); }}
              className="w-full text-xs font-sans text-text-tertiary hover:text-text-primary hover:bg-bg-hover py-1 rounded transition-colors"
            >
              Reset to zero
            </button>
          )}
        </PopoverContent>
      </Popover>

      {/* Loop bracket toggle */}
      <Tooltip content={loopActive ? 'Clear loop region' : 'Set loop region'}>
        <button
          data-testid="loop-toggle"
          onClick={loopActive ? clearLoop : enableLoop}
          className={`flex items-center gap-1.5 px-2 py-[5px] rounded-md text-xs font-sans transition-all duration-fast shrink-0 ${
            loopActive
              ? 'bg-accent-muted text-accent border border-accent/30'
              : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
          }`}
        >
          <BracketsCurly size={13} />
          <span className="hidden lg:block">Loop</span>
        </button>
      </Tooltip>

      {/* Timeline toggle */}
      <Tooltip content={timelineVisible ? 'Hide timeline (Y)' : 'Show timeline (Y)'}>
        <button
          data-testid="timeline-toggle-btn"
          onClick={toggleTimeline}
          className={`flex items-center gap-1.5 px-2 py-[5px] rounded-md text-xs font-sans transition-all duration-fast shrink-0 ${
            timelineVisible
              ? 'bg-accent-muted text-accent border border-accent/30'
              : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
          }`}
        >
          <Rows size={13} />
          <span className="hidden lg:block">Timeline</span>
        </button>
      </Tooltip>

      {/* Right panel toggle — mobile/tablet only (xl:hidden) */}
      {onToggleRight && (
        <button
          onClick={onToggleRight}
          className={`xl:hidden ml-auto w-[30px] h-[30px] flex items-center justify-center rounded-md transition-all duration-normal shrink-0 ${
            rightOpen
              ? 'bg-accent-muted border border-accent/30 text-accent'
              : 'bg-bg-input border border-border text-text-secondary hover:text-text-primary hover:bg-bg-hover'
          }`}
          title="Toggle right panel"
          data-testid="mobile-toggle-right"
        >
          <SidebarSimple size={14} className="scale-x-[-1]" />
        </button>
      )}
    </div>
  );
}
